import { useState, type FormEvent } from "react";
import { asDecimalString, Money } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createPromoCode, listPromoCodes } from "../../api/promoCodesApi";
import type { PromoCodeOut, PromoCodeType } from "../../api/types";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { canWritePromotions } from "../../roles/permissions";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

const COLUMNS: DataTableColumn<PromoCodeOut, keyof PromoCodeOut>[] = [
  { key: "code", header: "Code", render: (r) => <code>{r.code}</code> },
  { key: "type", header: "Type", render: (r) => r.type },
  {
    key: "value",
    header: "Value",
    align: "right",
    render: (r) => (r.type === "PERCENTAGE" ? `${r.value}%` : Money.format(r.value, TENANT_CURRENCY_FALLBACK)),
  },
  {
    key: "min_purchase_amount",
    header: "Min purchase",
    align: "right",
    render: (r) => (r.min_purchase_amount ? Money.format(r.min_purchase_amount, TENANT_CURRENCY_FALLBACK) : "—"),
  },
  { key: "usage_limit", header: "Usage limit", align: "right", render: (r) => r.usage_limit ?? "Unlimited" },
  { key: "expiry_date", header: "Expires", render: (r) => r.expiry_date ?? "—" },
  {
    key: "is_active",
    header: "Status",
    render: (r) => <span className={`badge ${r.is_active ? "badge--current" : "badge--overdue"}`}>{r.is_active ? "Active" : "Inactive"}</span>,
  },
];

/**
 * Promo codes — `GET/POST /promo-codes` (§15.2). Redemption itself happens at sale time
 * via `POST /sales`'s `promo_code` field (the till/POS side, not this app) — this page is
 * catalog management: list/create/toggle-active only. There is no `PATCH /promo-codes/{id}`
 * on the backend, so "toggle active" here creates a fresh row is NOT possible either —
 * only `is_active` at creation time is settable; a real deactivate-in-place action would
 * need a backend PATCH route that doesn't exist yet (documented in this app's
 * ARCHITECTURE.md as a real backend gap).
 */
export function PromoCodesPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWritePromotions(user.role) : false;
  const codesState = useAsyncData(() => ensureFreshToken().then((token) => listPromoCodes(token)), []);

  const [code, setCode] = useState("");
  const [type, setType] = useState<PromoCodeType>("PERCENTAGE");
  const [value, setValue] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [minPurchaseAmount, setMinPurchaseAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createPromoCode(token, {
        code: code.toUpperCase(),
        type,
        value: asDecimalString(value),
        expiry_date: expiryDate || undefined,
        usage_limit: usageLimit ? Number(usageLimit) : undefined,
        min_purchase_amount: minPurchaseAmount ? asDecimalString(minPurchaseAmount) : undefined,
      });
      setCode("");
      setValue("");
      setExpiryDate("");
      setUsageLimit("");
      setMinPurchaseAmount("");
      codesState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create promo code — it may already exist.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Promo Codes</h1>
          <p className="page__subtitle">Percentage or fixed-amount discount codes, redeemed at sale time.</p>
        </div>
      </div>

      {codesState.error ? <StatusBanner kind="error">{codesState.error}</StatusBanner> : null}
      {!canWrite && user ? (
        <StatusBanner kind="info">Your role ({user.role}) can view promo codes. Creating one is restricted to OWNER/BRANCH_MANAGER.</StatusBanner>
      ) : null}

      {canWrite ? (
        <div className="panel">
          <h2 className="panel__title">New promo code</h2>
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. SAVE10" required />
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="promo-type">
                  Type
                </label>
                <select id="promo-type" className="sl-input" value={type} onChange={(e) => setType(e.target.value as PromoCodeType)}>
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="FIXED_AMOUNT">Fixed amount</option>
                </select>
              </div>
              <Input
                label={type === "PERCENTAGE" ? "Value (%)" : "Value (currency)"}
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
              />
              <Input label="Min purchase amount" inputMode="decimal" value={minPurchaseAmount} onChange={(e) => setMinPurchaseAmount(e.target.value)} />
              <Input label="Usage limit" inputMode="numeric" value={usageLimit} onChange={(e) => setUsageLimit(e.target.value)} placeholder="Unlimited" />
              <Input label="Expiry date" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Creating…" : "Create promo code"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="panel">
        {codesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={COLUMNS} rows={codesState.data ?? []} getRowId={(row) => row.id} emptyMessage="No promo codes yet." caption="Promo Codes" />
        )}
      </div>
    </div>
  );
}
